import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Avatar,
  Divider,
  Card,
  CardContent,
  Tooltip,
  Button,
  IconButton,
  Stack,
  Alert,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Skeleton,
  Paper,
  Snackbar,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from '@mui/material';
import {
  EventAvailable,
  Cancel,
  History,
  InfoOutlined,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  CalendarToday,
  AccessTime,
  LocationOn,
  VideoCall,
  Edit,
  Delete,
  Warning,
  CheckCircle,
  Schedule,
  BookOnline,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAppointments } from '../../features/appointments/appointmentsSlice';
import { format, parseISO, isAfter, isBefore, addHours } from 'date-fns';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
// Import type definitions for JSDoc
import '../../types/api/appointments';
import '../../types/api/patient';
import '../../types/api/messages';
import '../../types/api/feedback';
import axiosInstance from '../../api/axiosInstance';

const AppointmentManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { data: appointments = [], loading, error } = useSelector((state) => state.appointments || {});
  
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [filterBy, setFilterBy] = useState('all');
  const [actionLoading, setActionLoading] = useState(false);
  const [bookingPolicy, setBookingPolicy] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({
    date: '',
    time: '',
    reason: ''
  });
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    dispatch(fetchAppointments());
    fetchBookingPolicy();
  }, [dispatch]);

  const fetchBookingPolicy = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/appointments/booking-policy/');
      setBookingPolicy(response.data);
    } catch (error) {
      console.error('Failed to fetch booking policy:', error);
    }
  }, []);

  const canReschedule = useCallback((appointment) => {
    if (!bookingPolicy) return false;
    const appointmentDate = parseISO(`${appointment.scheduled_date}T${appointment.scheduled_time}`);
    const cutoffTime = addHours(appointmentDate, -bookingPolicy.reschedule_hours);
    return isAfter(new Date(), cutoffTime);
  }, [bookingPolicy]);

  const canCancel = useCallback((appointment) => {
    if (!bookingPolicy) return false;
    const appointmentDate = parseISO(`${appointment.scheduled_date}T${appointment.scheduled_time}`);
    const cutoffTime = addHours(appointmentDate, -bookingPolicy.cancellation_hours);
    return isAfter(new Date(), cutoffTime);
  }, [bookingPolicy]);

  const getCancellationFee = useCallback((appointment) => {
    if (!bookingPolicy) return 0;
    const appointmentDate = parseISO(`${appointment.scheduled_date}T${appointment.scheduled_time}`);
    const cutoffTime = addHours(appointmentDate, -bookingPolicy.cancellation_hours);
    return isBefore(new Date(), cutoffTime) ? bookingPolicy.late_cancellation_fee : 0;
  }, [bookingPolicy]);

  const handleTabChange = (event, newValue) => setTabValue(newValue);
  
  const handleSearchChange = (event) => setSearchTerm(event.target.value);
  
  const handleSortClick = (event) => setSortAnchorEl(event.currentTarget);
  const handleSortClose = () => setSortAnchorEl(null);
  
  const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);
  
  const handleAppointmentClick = (appointment) => setSelectedAppointment(appointment);
  const handleCloseDetails = () => setSelectedAppointment(null);
  
  const handleRescheduleClick = (appointment) => {
    setSelectedAppointment(appointment);
    setRescheduleData({
      date: appointment.scheduled_date,
      time: appointment.scheduled_time,
      reason: ''
    });
    setShowRescheduleDialog(true);
  };

  const handleCancelClick = (appointment) => {
    setSelectedAppointment(appointment);
    setCancelReason('');
    setShowCancelDialog(true);
  };
  
  const handleRescheduleConfirm = useCallback(async () => {
    if (!selectedAppointment || !rescheduleData.date || !rescheduleData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setActionLoading(true);
    try {
      const idempotencyKey = uuidv4();
      const request = {
        scheduled_date: rescheduleData.date,
        scheduled_time: rescheduleData.time,
        reason: rescheduleData.reason,
        idempotency_key: idempotencyKey
      };

      await axiosInstance.post(
        `/appointments/${selectedAppointment.id}/reschedule/`,
        request,
        {
          headers: {
            'Idempotency-Key': idempotencyKey
          }
        }
      );

      toast.success('Appointment rescheduled successfully');
      setShowRescheduleDialog(false);
      setSelectedAppointment(null);
      dispatch(fetchAppointments()); // Refresh the list
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to reschedule appointment';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  }, [selectedAppointment, rescheduleData, dispatch]);
  
  const handleCancelConfirm = useCallback(async () => {
    if (!selectedAppointment || !cancelReason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }

    setActionLoading(true);
    try {
      const idempotencyKey = uuidv4();
      const request = {
        reason: cancelReason.trim(),
        idempotency_key: idempotencyKey
      };

      await axiosInstance.post(
        `/appointments/${selectedAppointment.id}/cancel/`,
        request,
        {
          headers: {
            'Idempotency-Key': idempotencyKey
          }
        }
      );

      toast.success('Appointment cancelled successfully');
      setShowCancelDialog(false);
      setSelectedAppointment(null);
      dispatch(fetchAppointments()); // Refresh the list
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to cancel appointment';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  }, [selectedAppointment, cancelReason, dispatch]);

  const filteredAppointments = Array.isArray(appointments) 
    ? appointments
        .filter((appointment) => {
          // Tab filtering
          if (tabValue === 0) {
            return appointment.status === 'Pending' || appointment.status === 'Confirmed';
          } else if (tabValue === 1) {
            return appointment.status === 'Confirmed' && new Date(appointment.scheduled_date) < new Date();
          } else if (tabValue === 2) {
            return appointment.status === 'Canceled';
          }
          return false;
        })
        .filter((appointment) => {
          // Search filtering
          if (!searchTerm) return true;
          const searchLower = searchTerm.toLowerCase();
          return (
            appointment.doctor_name.toLowerCase().includes(searchLower) ||
            appointment.type?.toLowerCase().includes(searchLower) ||
            appointment.location?.toLowerCase().includes(searchLower)
          );
        })
        .filter((appointment) => {
          // Additional filtering
          if (filterBy === 'all') return true;
          if (filterBy === 'online') return appointment.type === 'Online';
          if (filterBy === 'inPerson') return appointment.type === 'In-Person';
          return true;
        })
        .sort((a, b) => {
          // Sorting
          if (sortBy === 'date') {
            return new Date(b.scheduled_date) - new Date(a.scheduled_date);
          }
          if (sortBy === 'doctor') {
            return a.doctor_name.localeCompare(b.doctor_name);
          }
          return 0;
        })
    : [];

  const renderAppointmentCard = (appointment) => {
    const isUpcoming = appointment.status === 'Pending' || appointment.status === 'Confirmed';
    const isPast = appointment.status === 'Completed' || appointment.status === 'Cancelled';
    const canRescheduleAppointment = canReschedule(appointment);
    const canCancelAppointment = canCancel(appointment);
    const cancellationFee = getCancellationFee(appointment);

    return (
      <Card
        elevation={2}
        sx={{
          borderRadius: 2,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[4],
          },
          cursor: 'pointer',
          position: 'relative',
          overflow: 'visible',
        }}
        onClick={() => handleAppointmentClick(appointment)}
      >
        <CardContent>
          <Stack spacing={2}>
            {/* Doctor Info */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={appointment.doctor?.photo}
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '1.5rem',
                }}
              >
                {appointment.doctor?.name?.charAt(0) || appointment.doctor_name?.charAt(0) || 'D'}
              </Avatar>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Dr. {appointment.doctor?.name || appointment.doctor_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {appointment.doctor?.specializations?.join(', ') || 'General Practice'}
                </Typography>
                <Chip
                  icon={appointment.type === 'Online' ? <VideoCall /> : <LocationOn />}
                  label={appointment.type || 'In-Person'}
                  size="small"
                  color={appointment.type === 'Online' ? 'info' : 'default'}
                />
              </Box>
            </Stack>

            {/* Appointment Details */}
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CalendarToday fontSize="small" color="action" />
                <Typography variant="body2">
                  {format(parseISO(appointment.scheduled_date), 'EEEE, MMMM d, yyyy')}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AccessTime fontSize="small" color="action" />
                <Typography variant="body2">
                  {format(parseISO(`2000-01-01T${appointment.scheduled_time}`), 'h:mm a')}
                </Typography>
              </Stack>
              {appointment.clinic?.name && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocationOn fontSize="small" color="action" />
                  <Typography variant="body2" noWrap>
                    {appointment.clinic.name}
                  </Typography>
                </Stack>
              )}
            </Stack>

            {/* Status and Actions */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Chip
                label={appointment.status}
                color={
                  appointment.status === 'Confirmed'
                    ? 'success'
                    : appointment.status === 'Pending'
                    ? 'warning'
                    : appointment.status === 'Completed'
                    ? 'primary'
                    : 'error'
                }
                size="small"
                icon={
                  appointment.status === 'Confirmed' ? <CheckCircle /> :
                  appointment.status === 'Pending' ? <Schedule /> :
                  appointment.status === 'Completed' ? <CheckCircle /> :
                  <Cancel />
                }
              />
              <Typography variant="body2" color="text.secondary">
                ₹{appointment.consultation_fee}
              </Typography>
            </Stack>

            {/* Action Buttons */}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {isUpcoming && appointment.status === 'Confirmed' && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<VideoCall />}
                  onClick={(e) => {
                    e.stopPropagation();
                    toast.info('Video consultation feature coming soon');
                  }}
                >
                  Join
                </Button>
              )}
              
              {isUpcoming && canRescheduleAppointment && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRescheduleClick(appointment);
                  }}
                >
                  Reschedule
                </Button>
              )}
              
              {isUpcoming && canCancelAppointment && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelClick(appointment);
                  }}
                >
                  Cancel
                </Button>
              )}
              
              {!canRescheduleAppointment && !canCancelAppointment && isUpcoming && (
                <Chip
                  label="Past cutoff time"
                  color="warning"
                  size="small"
                  icon={<Warning />}
                />
              )}
            </Stack>

            {/* Cancellation Fee Warning */}
            {cancellationFee > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Late cancellation fee: ₹{cancellationFee}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderSkeletons = () => (
    <Grid container spacing={2}>
      {[1, 2, 3].map((index) => (
        <Grid item xs={12} key={index}>
          <Skeleton
            variant="rectangular"
            height={200}
            sx={{ borderRadius: 2 }}
          />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1200px', mx: 'auto' }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" gutterBottom={isMobile}>
          Your Appointments
        </Typography>
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to="/book-appointment"
          startIcon={<EventAvailable />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
          }}
        >
          Book New Appointment
        </Button>
      </Stack>

      {/* Tabs Navigation */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1rem',
              minHeight: 48,
            },
          }}
        >
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <EventAvailable />
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Upcoming</Box>
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <History />
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Past</Box>
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Cancel />
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Canceled</Box>
              </Stack>
            }
          />
        </Tabs>
      </Paper>

      {/* Search and Filters */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <TextField
          fullWidth
          placeholder="Search appointments..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<FilterListIcon />}
            onClick={handleFilterClick}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Filter
          </Button>
          <Button
            startIcon={<SortIcon />}
            onClick={handleSortClick}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Sort
          </Button>
        </Stack>
      </Stack>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        renderSkeletons()
      ) : filteredAppointments.length > 0 ? (
        <Grid container spacing={2}>
          {filteredAppointments.map((appointment) => (
            <Grid item xs={12} key={appointment.id}>
              {renderAppointmentCard(appointment)}
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 2,
          }}
        >
          <img
            src="/assets/no-appointments.svg"
            alt="No Appointments"
            style={{ maxWidth: '250px', marginBottom: '24px' }}
          />
          <Typography variant="h6" gutterBottom>
            No appointments found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm
              ? 'No appointments match your search criteria'
              : tabValue === 0
              ? "You don't have any upcoming appointments"
              : tabValue === 1
              ? "You don't have any past appointments"
              : "You don't have any canceled appointments"}
          </Typography>
          {tabValue === 0 && (
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/book-appointment"
              startIcon={<EventAvailable />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
              }}
            >
              Book an Appointment
            </Button>
          )}
        </Box>
      )}

      {/* Sort Menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleSortClose}
      >
        <MenuItem
          onClick={() => {
            setSortBy('date');
            handleSortClose();
          }}
        >
          Sort by Date
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSortBy('doctor');
            handleSortClose();
          }}
        >
          Sort by Doctor
        </MenuItem>
      </Menu>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
      >
        <MenuItem
          onClick={() => {
            setFilterBy('all');
            handleFilterClose();
          }}
        >
          All Appointments
        </MenuItem>
        <MenuItem
          onClick={() => {
            setFilterBy('online');
            handleFilterClose();
          }}
        >
          Online Consultations
        </MenuItem>
        <MenuItem
          onClick={() => {
            setFilterBy('inPerson');
            handleFilterClose();
          }}
        >
          In-Person Visits
        </MenuItem>
      </Menu>

      {/* Appointment Details Dialog */}
      <Dialog
        open={Boolean(selectedAppointment)}
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Appointment Details</DialogTitle>
        <DialogContent dividers>
          {selectedAppointment && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: theme.palette.primary.main,
                    fontSize: '1.75rem',
                  }}
                >
                  {selectedAppointment.doctor_name.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {selectedAppointment.doctor_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedAppointment.specialization || 'General Practitioner'}
                  </Typography>
                </Box>
              </Stack>

              <Divider />

              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body2">
                    {format(
                      new Date(selectedAppointment.scheduled_date),
                      'MMMM d, yyyy'
                    )}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Time
                  </Typography>
                  <Typography variant="body2">
                    {selectedAppointment.formatted_time}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Type
                  </Typography>
                  <Chip
                    size="small"
                    icon={
                      selectedAppointment.type === 'Online' ? (
                        <VideoCall />
                      ) : (
                        <LocationOn />
                      )
                    }
                    label={selectedAppointment.type || 'In-Person'}
                    color={selectedAppointment.type === 'Online' ? 'info' : 'default'}
                  />
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    size="small"
                    label={selectedAppointment.status}
                    color={
                      selectedAppointment.status === 'Confirmed'
                        ? 'success'
                        : selectedAppointment.status === 'Pending'
                        ? 'warning'
                        : 'error'
                    }
                  />
                </Stack>

                {selectedAppointment.location && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body2">
                      {selectedAppointment.location}
                    </Typography>
                  </Stack>
                )}

                {selectedAppointment.notes && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Notes
                    </Typography>
                    <Typography variant="body2">
                      {selectedAppointment.notes}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {selectedAppointment?.status !== 'Canceled' && (
            <Button
              color="error"
              onClick={() => handleCancelClick(selectedAppointment)}
            >
              Cancel Appointment
            </Button>
          )}
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog
        open={showRescheduleDialog}
        onClose={() => setShowRescheduleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {selectedAppointment && (
              <Alert severity="info">
                Rescheduling appointment with Dr. {selectedAppointment.doctor?.name || selectedAppointment.doctor_name}
              </Alert>
            )}
            
            <TextField
              label="Date"
              type="date"
              value={rescheduleData.date}
              onChange={(e) => setRescheduleData(prev => ({ ...prev, date: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
            />
            
            <TextField
              label="Time"
              type="time"
              value={rescheduleData.time}
              onChange={(e) => setRescheduleData(prev => ({ ...prev, time: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label="Reason for rescheduling (optional)"
              multiline
              rows={3}
              value={rescheduleData.reason}
              onChange={(e) => setRescheduleData(prev => ({ ...prev, reason: e.target.value }))}
              fullWidth
              placeholder="Please provide a reason for rescheduling..."
            />
            
            {bookingPolicy && (
              <Alert severity="warning">
                You can reschedule up to {bookingPolicy.reschedule_hours} hours before the appointment.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRescheduleDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRescheduleConfirm}
            disabled={actionLoading || !rescheduleData.date || !rescheduleData.time}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Schedule />}
          >
            {actionLoading ? 'Rescheduling...' : 'Reschedule Appointment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {selectedAppointment && (
              <Alert severity="warning">
                Cancelling appointment with Dr. {selectedAppointment.doctor?.name || selectedAppointment.doctor_name}
              </Alert>
            )}
            
            <TextField
              label="Reason for cancellation"
              multiline
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              fullWidth
              placeholder="Please provide a reason for cancellation..."
              required
            />
            
            {selectedAppointment && getCancellationFee(selectedAppointment) > 0 && (
              <Alert severity="error">
                Late cancellation fee: ₹{getCancellationFee(selectedAppointment)}
              </Alert>
            )}
            
            {bookingPolicy && (
              <Alert severity="info">
                You can cancel up to {bookingPolicy.cancellation_hours} hours before the appointment without any fee.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)} disabled={actionLoading}>
            Keep Appointment
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleCancelConfirm}
            disabled={actionLoading || !cancelReason.trim()}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Cancel />}
          >
            {actionLoading ? 'Cancelling...' : 'Cancel Appointment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentManagement;
